import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Search, Calendar as CalendarIcon, Play, Download, Filter, X, ChevronLeft, ChevronRight, Loader2, Cloud, CloudOff, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchDashboard = () => {
  const { api } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filters
  const [trackingId, setTrackingId] = useState('');
  const [selectedExecutive, setSelectedExecutive] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState('all');

  // Video player
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchExecutives();
    fetchRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExecutives = async () => {
    try {
      const res = await api.get('/executives');
      setExecutives(res.data.executives);
    } catch (err) {
      console.error('Failed to fetch executives:', err);
    }
  };

  const fetchRecordings = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 15);
      if (trackingId) params.append('trackingId', trackingId);
      if (selectedExecutive && selectedExecutive !== 'all') params.append('executiveId', selectedExecutive);
      if (startDate) params.append('startDate', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('endDate', format(endDate, 'yyyy-MM-dd'));
      if (status && status !== 'all') params.append('status', status);

      const res = await api.get(`/recordings?${params.toString()}`);
      setRecordings(res.data.recordings);
      setPagination({
        page: res.data.page,
        pages: res.data.pages,
        total: res.data.total
      });
    } catch (err) {
      toast.error('Failed to fetch recordings');
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    fetchRecordings(1);
  };

  const clearFilters = () => {
    setTrackingId('');
    setSelectedExecutive('all');
    setStartDate(null);
    setEndDate(null);
    setStatus('all');
    setTimeout(() => fetchRecordings(1), 100);
  };

  const playVideo = (recording) => {
    if (!recording?.cloudUrl) {
      toast.error('Video file not available');
      return;
    }

    setSelectedVideo(recording);
    setVideoModal(true);
  };

  const downloadVideo = (recording) => {
    if (!recording?.cloudUrl) {
      toast.error('Video file not available');
      return;
    }

    const a = document.createElement('a');
    a.href = recording.cloudUrl;
    a.download = `${recording.trackingId}-${format(new Date(recording.createdAt), 'yyyy-MM-dd')}.webm`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    toast.success('Download started');
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const statusBadge = (rec) => {
    const status = rec.status;

    if (status === 'compressing') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span className="text-xs">{rec.compressionProgress || 0}%</span>
        </div>
      );
    }

    if (status === 'uploading') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border bg-blue-500/20 text-blue-400 border-blue-500/30">
          <Cloud className="w-3 h-3 animate-pulse" />
          <span className="text-xs">{rec.uploadProgress || 0}%</span>
        </div>
      );
    }

    if (status === 'uploaded') {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border bg-purple-500/20 text-purple-400 border-purple-500/30">
          <Cloud className="w-3 h-3" />
          <span className="text-xs">uploaded</span>
        </div>
      );
    }

    const styles = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      recording: 'bg-red-500/20 text-red-400 border-red-500/30',
      compressed: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-md border ${styles[status] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="search-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Search Recordings</h1>
          <p className="text-zinc-400 mt-1">Find and manage video recordings</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="border-b border-zinc-800 pb-4">
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-400" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Tracking ID</label>
              <Input
                placeholder="Search by ID..."
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                className="bg-zinc-950 border-zinc-800"
                data-testid="search-tracking-id"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Executive</label>
              <Select value={selectedExecutive} onValueChange={setSelectedExecutive}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="search-executive">
                  <SelectValue placeholder="All executives" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All executives</SelectItem>
                  {executives.map(exec => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-zinc-950 border-zinc-800 text-left font-normal" data-testid="search-start-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start bg-zinc-950 border-zinc-800 text-left font-normal" data-testid="search-end-date">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Pick date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="search-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="recording">Recording</SelectItem>
                  <SelectItem value="compressed">Compressed</SelectItem>
                  <SelectItem value="uploaded">Uploaded</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2 lg:col-span-5">
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-500" data-testid="search-btn">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              <Button type="button" variant="outline" onClick={clearFilters} className="border-zinc-700" data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Tracking ID</TableHead>
                  <TableHead className="text-zinc-400">Executive</TableHead>
                  <TableHead className="text-zinc-400">Date & Time</TableHead>
                  <TableHead className="text-zinc-400">Duration</TableHead>
                  <TableHead className="text-zinc-400">Size</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : recordings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                      No recordings found
                    </TableCell>
                  </TableRow>
                ) : (
                  recordings.map((rec) => (
                    <TableRow key={rec.id} className="border-zinc-800" data-testid={`recording-row-${rec.id}`}>
                      <TableCell className="font-mono text-indigo-400">{rec.trackingId}</TableCell>
                      <TableCell className="text-zinc-300">{rec.executiveName}</TableCell>
                      <TableCell className="text-zinc-400">{format(new Date(rec.createdAt), 'PPp')}</TableCell>
                      <TableCell className="font-mono text-zinc-400">{formatDuration(rec.duration)}</TableCell>
                      <TableCell className="text-zinc-400">{formatFileSize(rec.fileSize)}</TableCell>
                      <TableCell>{statusBadge(rec)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => playVideo(rec)}
                            disabled={!rec.cloudUrl}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            data-testid={`play-btn-${rec.id}`}
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadVideo(rec)}
                            disabled={!rec.cloudUrl}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                            data-testid={`download-btn-${rec.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <p className="text-sm text-zinc-400">
                Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchRecordings(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="border-zinc-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchRecordings(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="border-zinc-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Video Player Modal */}
      <Dialog open={videoModal} onOpenChange={setVideoModal}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800" data-testid="video-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Recording: <span className="text-indigo-400 font-mono">{selectedVideo?.trackingId}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {selectedVideo?.cloudUrl ? (
              <video
                src={selectedVideo.cloudUrl}
                controls
                className="w-full rounded-lg bg-black"
                data-testid="video-player"
              />
            ) : (
              <p className="text-zinc-500">Video file not available</p>
            )}
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-zinc-500">Executive</p>
                <p className="text-zinc-300">{selectedVideo?.executiveName}</p>
              </div>
              <div>
                <p className="text-zinc-500">Date</p>
                <p className="text-zinc-300">{selectedVideo && format(new Date(selectedVideo.createdAt), 'PPp')}</p>
              </div>
              <div>
                <p className="text-zinc-500">Duration</p>
                <p className="text-zinc-300 font-mono">{formatDuration(selectedVideo?.duration)}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => downloadVideo(selectedVideo)} className="bg-indigo-600 hover:bg-indigo-500">
                <Download className="w-4 h-4 mr-2" />
                Download Video
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SearchDashboard;
